import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Briefcase, FileText, Loader2, Mail, Search, User as UserIcon } from 'lucide-react'
import { api } from '../lib/api'
import type { SearchHit, SearchHitType, SearchResponse } from '@toga/shared'

const DEBOUNCE_MS = 220

const GROUP_LABEL: Record<keyof SearchResponse['groups'], string> = {
  clients: 'Clientes',
  cases: 'Expedientes',
  documents: 'Documentos',
  emails: 'Correos',
}

const ICONS: Record<SearchHitType, typeof Briefcase> = {
  CLIENT: UserIcon,
  CASE: Briefcase,
  DOCUMENT: FileText,
  EMAIL: Mail,
}

interface Props {
  isLight: boolean
}

export default function GlobalSearch({ isLight }: Props) {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<SearchResponse | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Debounce de la query
  useEffect(() => {
    if (q.trim().length < 2) {
      setData(null)
      return
    }
    const ctrl = new AbortController()
    const t = window.setTimeout(async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams({ q: q.trim() })
        const resp = await api.get<SearchResponse>(`/search?${params.toString()}`)
        if (!ctrl.signal.aborted) setData(resp)
      } catch {
        if (!ctrl.signal.aborted) setData(null)
      } finally {
        if (!ctrl.signal.aborted) setLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => {
      window.clearTimeout(t)
      ctrl.abort()
    }
  }, [q])

  // Click fuera para cerrar
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // Shortcut Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        setOpen(true)
      } else if (e.key === 'Escape') {
        setOpen(false)
        inputRef.current?.blur()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const onPick = useCallback(
    (hit: SearchHit) => {
      setOpen(false)
      setQ('')
      navigate(hit.href)
    },
    [navigate]
  )

  const groups = data?.groups
  const isEmpty =
    !loading &&
    q.trim().length >= 2 &&
    groups &&
    groups.clients.length === 0 &&
    groups.cases.length === 0 &&
    groups.documents.length === 0 &&
    groups.emails.length === 0

  return (
    <div className="relative" ref={containerRef}>
      <Search
        size={16}
        className={[
          'absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none',
          isLight ? 'text-slate-400' : 'text-slate-500',
        ].join(' ')}
      />
      <input
        ref={inputRef}
        type="text"
        value={q}
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        placeholder="Buscar clientes, expedientes, documentos…"
        className={[
          'w-96 pl-9 pr-12 py-2 text-sm rounded-full focus:outline-none focus:ring-1 focus:ring-toga-500/40 transition',
          isLight
            ? 'bg-white/80 border border-slate-200 text-slate-700 placeholder-slate-400 focus:border-toga-500/60'
            : 'bg-slate-800/60 border border-slate-700/60 text-slate-200 placeholder-slate-500 focus:border-toga-500/60',
        ].join(' ')}
      />
      <kbd
        className={[
          'absolute right-3 top-1/2 -translate-y-1/2 text-[10px] px-1.5 py-0.5 rounded border pointer-events-none',
          isLight
            ? 'border-slate-200 text-slate-400'
            : 'border-slate-700 text-slate-500 bg-slate-900/50',
        ].join(' ')}
      >
        ⌘K
      </kbd>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute left-0 right-0 mt-2 w-[28rem] max-h-[70vh] overflow-y-auto surface-deep p-2 shadow-xl animate-slideUp z-50"
          style={{ background: 'var(--bg-surface-strong)' }}
        >
          {loading && (
            <div className="flex items-center gap-2 px-3 py-3 text-xs t-muted">
              <Loader2 size={12} className="animate-spin" /> Buscando…
            </div>
          )}
          {isEmpty && !loading && (
            <div className="px-3 py-4 text-xs t-faint text-center">Sin resultados</div>
          )}
          {groups && !loading && !isEmpty && (
            <>
              {(['cases', 'clients', 'documents', 'emails'] as const).map((key) => {
                const items = groups[key]
                if (items.length === 0) return null
                return (
                  <div key={key} className="mb-2 last:mb-0">
                    <div className="text-[10px] uppercase tracking-wider t-faint px-3 py-1">
                      {GROUP_LABEL[key]}
                    </div>
                    <ul>
                      {items.map((hit) => {
                        const Icon = ICONS[hit.type]
                        return (
                          <li key={`${hit.type}-${hit.id}`}>
                            <button
                              type="button"
                              onClick={() => onPick(hit)}
                              className="w-full text-left px-3 py-2 rounded hover:bg-hover flex items-start gap-2"
                            >
                              <Icon size={14} className="t-faint mt-0.5 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm t-primary truncate">{hit.title}</div>
                                {hit.subtitle && (
                                  <div className="text-[11px] t-faint truncate">
                                    {hit.subtitle}
                                  </div>
                                )}
                                {hit.snippet && (
                                  <div className="text-[11px] t-muted truncate mt-0.5">
                                    {hit.snippet}
                                  </div>
                                )}
                              </div>
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )
              })}
            </>
          )}
        </div>
      )}
    </div>
  )
}
